import { Button, tokens } from "@blurb/codex-react";
import { createStyles } from "@mantine/core";
import { GraphQL, useRpiGraphqlRequest } from "@rpi/openapi-api";
import { RpiAlert, useShowErrorToast, useShowSuccessToast } from "@rpi/openapi-core";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { RpaButtonLink } from "../../../components/RpiLink";
import { ConfirmationModalV2 } from "../../../components/modals/ConfirmationModalV2";
import { useAuthUser } from "../../../hooks/useAuthUser";
import { RpiNotification, useRpiNotifications } from "../../../hooks/useRpiNotifications";
import { RpaDrawer } from "../../../rpi-core/drawer/RpaDrawer";
import { RpiDrawerProps, createRpiDrawerView } from "../../../rpi-core/drawer/RpiDrawer";
import { TaxFileDropzone } from "./TaxFileDropzone";
import { TaxFileRow, TaxFileRowState } from "./TaxFileRow";
import { TaxUpload, useTaxExemptUpload } from "./useTaxExemptUpload";

const DRAWER_TITLE = "Upload Tax-Exempt Documents";
const CANCEL_CONFIRM_TITLE = "Cancel Submission?";
const CANCEL_CONFIRM_MESSAGE =
  "Are you sure you want to cancel the submission of your Tax-Exempt Document? Any files you uploaded will be discarded.";
const NO_CUSTOMER_MESSAGE = "We couldn't find a customer on your account, so tax-exempt documents can't be uploaded.";
const SUBMIT_SUCCESS_MESSAGE = "Your tax-exempt document(s) was submitted successfully!";
const SUBMIT_ERROR_MESSAGE = "Something went wrong. Please try again.";

const useStyles = createStyles({
  body: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[4],
    padding: `0 ${tokens.spacing[6]}`,
  },
  footer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacing[2],
    padding: tokens.spacing[6],
  },
  message: {
    padding: `0 ${tokens.spacing[6]}`,
  },
});

function uploadRowState(upload: TaxUpload): TaxFileRowState {
  switch (upload.status) {
    case "uploading":
      return { kind: "uploading", progress: upload.progress, fileSize: upload.file.size };
    case "uploaded":
      return { kind: "uploaded" };
    case "failed":
      return { kind: "failed" };
    case "rejected":
      return { kind: "rejected", reason: upload.reason ?? "" };
  }
}

interface TaxExemptUploadFormProps {
  customerPublicId: string;
  onClose: () => void;
}

export const TaxExemptUploadForm: React.FC<TaxExemptUploadFormProps> = ({ customerPublicId, onClose }) => {
  const { classes } = useStyles();
  const notifications = useRpiNotifications();
  const showSuccessToast = useShowSuccessToast();
  const showErrorToast = useShowErrorToast();
  const [cancelConfirmOpen, setCancelConfirmOpen] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { uploads, addFiles, retry, remove, discardAll, isUploading, hasUploaded, hasFailed } =
    useTaxExemptUpload(customerPublicId);

  const submitForApproval = useRpiGraphqlRequest<
    GraphQL.SubmitTaxDocumentsForApprovalMutation,
    GraphQL.SubmitTaxDocumentsForApprovalMutationVariables
  >(GraphQL.SubmitTaxDocumentsForApprovalDocument);

  const queryClient = useQueryClient();
  const refreshTaxBanner = () => notifications.refetch(RpiNotification.TAX_DOCUMENT_NOT_UPLOADED);
  // The settings card reads DashboardUserCurrent with staleTime: Infinity, so without an explicit
  // invalidation the Tax Exemption section keeps showing the pre-submit state after the drawer closes.
  const refreshAccountSettings = () =>
    queryClient.invalidateQueries({ queryKey: ["DashboardUserCurrent"] }).catch(() => undefined);

  // Files upload on selection; Submit for Approval is the explicit submission
  // signal — it transitions the customer's tax review to SUBMITTED and notifies
  // Finance (ING-834). On failure the drawer stays open: the documents are
  // already uploaded, so the user only needs to retry the submit itself.
  const submit = async () => {
    if (hasFailed) {
      showErrorToast(SUBMIT_ERROR_MESSAGE);
      return;
    }
    setIsSubmitting(true);
    try {
      await submitForApproval({});
      showSuccessToast(SUBMIT_SUCCESS_MESSAGE);
      refreshTaxBanner();
      void refreshAccountSettings();
      onClose();
    } catch {
      showErrorToast(SUBMIT_ERROR_MESSAGE);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Only warn before discarding real upload work; rejected-only staging can close silently.
  const hasUploadWork = isUploading || hasUploaded;

  const requestCancel = () => {
    if (hasUploadWork) {
      setCancelConfirmOpen(true);
      return;
    }
    void discardAll();
    onClose();
  };

  const confirmCancel = async () => {
    setCancelConfirmOpen(false);
    await discardAll();
    refreshTaxBanner();
    onClose();
  };

  return (
    <>
      <div className={classes.body}>
        <TaxFileDropzone onFiles={addFiles} />
        {uploads.map((upload) => (
          <TaxFileRow
            key={upload.id}
            fileName={upload.file.name}
            state={uploadRowState(upload)}
            onRemove={() => remove(upload.id)}
            onRetry={() => retry(upload.id)}
          />
        ))}
      </div>

      <RpaDrawer.Footer>
        <div className={classes.footer}>
          <Button
            variant="filled"
            intent="primary"
            size="large"
            fullWidth
            disabled={!hasUploaded || isUploading || isSubmitting}
            onClick={() => void submit()}
          >
            Submit for Approval
          </Button>
          <RpaButtonLink data-testid="cancel-tax-upload" onClick={requestCancel}>
            Cancel
          </RpaButtonLink>
        </div>
      </RpaDrawer.Footer>

      <ConfirmationModalV2
        opened={cancelConfirmOpen}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => void confirmCancel()}
        title={CANCEL_CONFIRM_TITLE}
        message={CANCEL_CONFIRM_MESSAGE}
        cancelLabel="Cancel"
        confirmLabel="Yes, Cancel Submission"
        confirmIntent="danger"
      />
    </>
  );
};

export const SettingsTaxExemptUploadView = createRpiDrawerView<Pick<RpiDrawerProps, "opened" | "onClose">>(
  ({ opened, onClose }) => {
    const { classes } = useStyles();
    const user = useAuthUser();
    const hasNoCustomer = !user.isDashboardUserLoading && !user.id;

    return (
      <RpaDrawer title={DRAWER_TITLE} opened={opened} onClose={onClose} isLoading={user.isDashboardUserLoading}>
        {user.id && <TaxExemptUploadForm customerPublicId={user.id} onClose={onClose} />}
        {hasNoCustomer && (
          <div className={classes.message}>
            <RpiAlert>{NO_CUSTOMER_MESSAGE}</RpiAlert>
          </div>
        )}
      </RpaDrawer>
    );
  }
);
